import {
    Box,
    Chip,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Accessibility as BodyPartIcon,
    Build as EquipmentIcon,
    CalendarToday as BlocksIcon,
    Description as DescriptionIcon,
    Edit as EditIcon,
    EmojiEvents as GoalsIcon,
    FitnessCenter as TemplateIcon,
    Groups as MuscleIcon,
    Language as LanguageIcon,
    Person as PersonIcon,
    Schedule as WeeksIcon,
    Star as ExperienceIcon
} from "@mui/icons-material";
import {useState} from "react";
import {experienceLevels} from "../../enums/ExperienceLevels";
import {EquipmentTypes} from "../../enums/EquipmentType";
import {bodyParts} from "../../enums/BodyParts";
import {SecondaryMuscles} from "../../enums/SecondaryMuscles";
import {TemplateGoals} from "../../enums/TemplateGoals";
import CreateTemplateDialog from "../CreateTemplateDialog";

/**
 * Read-only summary of a NEW_TEMPLATE's metadata, with an edit button that opens
 * CreateTemplateDialog.
 *
 * The three `*באנגלית` rows this used to show (name / description / warmup text) were removed when
 * the translation panel landed. They rendered the legacy English COLUMNS, which stop being the
 * English source of truth the moment anyone edits English in the panel — so the summary would have
 * confidently displayed a stale value. Translations are read and written in one place: the panel,
 * inside the edit dialog.
 */
const TemplateMetadataSection = ({
                                     template,
                                     onTemplateUpdate,
                                 }) => {
    const [editOpen, setEditOpen] = useState(false);


    // Default template structure if none provided
    const defaultTemplate = {
        id: null,
        name: "",
        nameHeb: "",
        gender: null,
        experienceLevel: null,
        bodyPart: null,
        secondaryMuscle: null,
        secondaryMuscle2: null,
        equipmentType: "GYM",
        notes: null,
        description: "",
        descriptionHeb: "",
        goals: [],
        numberOfBlocks: null,
        numberOfWeeksInBlock: null
    };

    const currentTemplate = template || defaultTemplate;

    const handleTemplateSubmit = (updatedTemplate) => {
        if (onTemplateUpdate) {
            onTemplateUpdate(updatedTemplate);
        }
        setEditOpen(false);
    };

    const getGenderText = (gender) => {
        switch (gender) {
            case "FEMALE":
                return "נקבה";
            case "MALE":
                return "זכר";
            default:
                return "לא נבחר";
        }
    };

    const getExperienceLevelName = (value) => {
        const found = experienceLevels.find(level => level.value === value);
        return found ? found.name : "לא נבחר";
    };

    const getBodyPartName = (value) => {
        const found = bodyParts.find(bp => bp.value === value);
        return found ? found.name : "לא נבחר";
    };

    const getSecondaryMuscleName = (value) => {
        const found = SecondaryMuscles.find(sm => sm.value === value);
        return found ? found.name : "לא נבחר";
    };

    const getEquipmentTypeName = (value) => {
        const found = EquipmentTypes.find(eq => eq.value === value);
        return found ? found.name : "לא נבחר";
    };

    const getGoalsText = (goals) => {
        if (!goals || goals.length === 0) return "לא נבחר";
        return goals.map(goal => TemplateGoals[goal] || goal).join(", ");
    };

    const getNumberText = (value) => {
        return value !== null && value !== undefined ? value.toString() : "לא הוגדר";
    };

    const isTemplateComplete = () => {
        // The English name is no longer part of "complete": it is a translation row now, written
        // through the panel in the edit dialog, not a required field on the program.
        return currentTemplate.nameHeb &&
            currentTemplate.experienceLevel &&
            currentTemplate.bodyPart &&
            currentTemplate.goals &&
            currentTemplate.goals.length > 0;
    };

    return (
        <>
            <Paper
                sx={{
                    backgroundColor: "#FCFCFD",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    direction: "rtl",
                    mb: 2
                }}
                elevation={0}
            >

                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: "#F8FAFC",
                        borderBottom: "1px solid #E2E8F0",
                        cursor: "pointer"
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TemplateIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#1E293B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            נתוני תוכנית
                        </Typography>
                        {!isTemplateComplete() && (
                            <Chip
                                label="חסרים פרטים"
                                size="small"
                                sx={{
                                    backgroundColor: "#FEF3C7",
                                    color: "#D97706",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: "20px",
                                    mr: 1
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title="עריכת פרטים">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditOpen(true);
                                }}
                                sx={{
                                    color: "#64748B",
                                    "&:hover": {
                                        backgroundColor: "#E2E8F0",
                                        color: "#475569"
                                    }
                                }}
                                size="small"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ padding: "20px", paddingBottom: "12px"}}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: "repeat(4, 1fr)"
                            },
                            gap: "16px 24px",
                            rowGap: "12px"
                        }}
                    >
                        {/* Essential Information - Always Visible */}
                        <ProfileRow
                            icon={<LanguageIcon />}
                            label="שם התוכנית"
                            value={currentTemplate.nameHeb || "לא הוגדר"}
                            required={true}
                        />

                        <ProfileRow
                            icon={<ExperienceIcon />}
                            label="רמת מתאמן"
                            value={getExperienceLevelName(currentTemplate.experienceLevel)}
                            required={true}
                        />

                        <ProfileRow
                            icon={<BodyPartIcon />}
                            label="שריר ראשי"
                            value={getBodyPartName(currentTemplate.bodyPart)}
                            required={true}
                        />

                        <ProfileRow
                            icon={<EquipmentIcon />}
                            label="ציוד קיים"
                            value={getEquipmentTypeName(currentTemplate.equipmentType)}
                            required={true}
                        />
                    </Box>
                </Box>

                <Box sx={{ padding: "0 20px 20px 20px" }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: "repeat(4, 1fr)"
                            },
                            gap: "16px 24px",
                            rowGap: "12px",
                            paddingTop: "8px",
                        }}
                    >
                        <ProfileRow
                            icon={<PersonIcon />}
                            label="מגדר"
                            value={getGenderText(currentTemplate.gender)}
                        />

                        <ProfileRow
                            icon={<MuscleIcon />}
                            label="שריר ספציפי"
                            value={getSecondaryMuscleName(currentTemplate.secondaryMuscle)}
                        />

                        <ProfileRow
                            icon={<MuscleIcon />}
                            label="שריר ספציפי 2"
                            value={getSecondaryMuscleName(currentTemplate.secondaryMuscle2)}
                        />

                        {/* New fields for blocks and weeks */}
                        <ProfileRow
                            icon={<BlocksIcon />}
                            label="מספר בלוקים"
                            value={getNumberText(currentTemplate.numberOfBlocks)}
                        />

                        <ProfileRow
                            icon={<WeeksIcon />}
                            label="מספר שבועות בבלוק"
                            value={getNumberText(currentTemplate.numberOfWeeksInBlock)}
                        />

                        {/* Goals - spans multiple columns */}
                        <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}>
                            <ProfileRow
                                icon={<GoalsIcon />}
                                label="מטרות"
                                value={getGoalsText(currentTemplate.goals)}
                                required={true}
                            />
                        </Box>

                        {/* Descriptions - spans remaining space */}
                        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 2" } }}>
                            <ProfileRow
                                icon={<DescriptionIcon />}
                                label="תיאור"
                                value={currentTemplate.descriptionHeb || "אין תיאור"}
                            />
                        </Box>

                        {/* Warmup copy — shown to the trainee at the top of the workout screen. */}
                        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 2" } }}>
                            <ProfileRow
                                icon={<DescriptionIcon />}
                                label="טקסט חימום"
                                value={currentTemplate.warmupTextHeb || "אין טקסט חימום"}
                            />
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Edit Dialog */}
            {editOpen && (
                <CreateTemplateDialog
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    onSave={handleTemplateSubmit}
                    template={currentTemplate}
                    isEditMode={true}
                />
            )}
        </>
    );
};

const ProfileRow = ({ icon, label, value, required = false }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: "24px" }}>
        <Box
            sx={{
                color: "#64748B",
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                flexShrink: 0,
                marginTop: "1px"
            }}
        >
            {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#64748B",
                    marginBottom: "2px",
                    textAlign: "right"
                }}
            >
                {required && "*"}{label}:
            </Typography>
            <Box sx={{ textAlign: "right" }}>
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: value === "לא הוגדר" || value === "לא נבחר" ? "#EF4444" : "#1E293B",
                        lineHeight: "18px",
                        wordBreak: "break-word"
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    </Box>
);

export default TemplateMetadataSection;